import { isImpersonated } from "@clockwise/client-commons/src/util/jwt";
import { MentionsV2 } from "@clockwise/schema";
import { jwt } from "@clockwise/web-commons/src/util/local-storage";
import React, { useEffect } from "react";
import { useFeatureFlag } from "../../../launch-darkly/useLaunchDarkly";
import { AI_EXCLUSION_TOOLTIP, AI_IMPERSONATION_TOOLTIP } from "../constants";
import { useCancelProcessMessage } from "../hooks/useCancelProcessMessage";
import { ChatInputSuggestions } from "../web/ChatInputSuggestions";
import { ChatUserInput } from "../web/ChatUserInput";
import { ChatInput } from "./ChatInput";
import { useAIMessageContext } from "./hooks/AIMessageContext";
import { useFocusChatTextInput } from "./hooks/useFocusChatTextInput";
import { useProcessMessage } from "./hooks/useProcessMessage";
import { useScrollChatToBottom } from "./hooks/useScrollChatToBottom";

export const BOTTOM_OF_CHAT_NODE_ID = "cw-bottom-of-chat";

export const AIChatBox = () => {
  const {
    processingMessage,
    messages,
    debugModel,
    containsActiveProposal,
    loadingHistory,
    shouldExcludeAi,
  } = useAIMessageContext();
  const scrollChatToBottom = useScrollChatToBottom();
  const focusChatTextInput = useFocusChatTextInput();
  const previousMessagesLengthRef = React.useRef(messages.length);
  const [hasChatInputV2] = useFeatureFlag("rollout-ai-chat-chat-input-v-2");

  const { processMessage } = useProcessMessage(focusChatTextInput);
  const cancelProcessing = useCancelProcessMessage(scrollChatToBottom);
  const isImpersonatedUser = isImpersonated(jwt.get());

  // scroll to bottom if possible on first load
  useEffect(() => {
    if (loadingHistory) {
      // We want to wait until the history is loaded before scrolling to the bottom
      return;
    }

    scrollChatToBottom(600);
  }, [loadingHistory]); // eslint-disable-line react-hooks/exhaustive-deps

  // scroll to bottom if new message loads in from the extension gCal button
  useEffect(() => {
    if (previousMessagesLengthRef.current < messages.length) {
      scrollChatToBottom();
    }

    previousMessagesLengthRef.current = messages.length;
  }, [previousMessagesLengthRef.current, messages.length]);

  const submitMessage = async (
    currentText: string,
    mentions: MentionsV2,
    cql?: string | null | undefined,
  ) => {
    scrollChatToBottom();
    let submitText = currentText;
    if (debugModel !== "default") {
      submitText = `[${debugModel}] ${currentText}`;
    }

    void processMessage(submitText, mentions, cql);
  };

  const cancelProcessMessage = () => {
    void cancelProcessing();
  };

  const focusInput = messages.length === 0 && !processingMessage;

  const showChatInputSuggestions =
    !containsActiveProposal && !processingMessage && messages.length !== 0 && !shouldExcludeAi;

  return (
    <>
      <div className="cw-justify-self-end cw-mt-8 cw-sticky cw-bottom-0 cw-bg-neutral cw-bg-opacity-70 cw-backdrop-blur-md">
        {showChatInputSuggestions && <ChatInputSuggestions />}
        {hasChatInputV2 ? (
          // TODO: active development @scott in https://linear.app/getclockwise/issue/AI-4761/[epic]-chat-input-improvements
          <div className="cw-px-4 cw-w-full cw-pb-4">
            <ChatInput
              disabled={isImpersonatedUser || shouldExcludeAi}
              disabledReason={
                isImpersonatedUser
                  ? AI_IMPERSONATION_TOOLTIP
                  : shouldExcludeAi
                  ? AI_EXCLUSION_TOOLTIP
                  : undefined
              }
              focusInput={focusInput}
              hasActiveProposal={containsActiveProposal}
              loading={processingMessage}
              onCancel={cancelProcessMessage}
              onSubmit={submitMessage}
            />
          </div>
        ) : (
          <div className="cw-px-4 cw-w-full cw-pb-4">
            <ChatUserInput
              submitMessage={submitMessage}
              loading={processingMessage}
              focusInput={focusInput}
              disabled={isImpersonatedUser || shouldExcludeAi}
              cancelMessage={cancelProcessMessage}
            />
            <div className="cw-mt-2 cw-body-sm cw-text-muted cw-text-center cw-text-11">
              {isImpersonatedUser
                ? "Chat user input and calendar event manipulation are deactivated because you are impersonating a user via sudo"
                : "Nothing will change on your calendar until you explicitly confirm it"}
            </div>
          </div>
        )}
      </div>
      <span id={BOTTOM_OF_CHAT_NODE_ID} />
    </>
  );
};
